import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import {
  ArrowPathIcon,
  PlayIcon,
  CheckCircleIcon,
  ExclamationCircleIcon,
  LockClosedIcon,
  SparklesIcon
} from '@heroicons/react/24/outline';
import { useTriggerTier, useTriggerAllTiers } from '../../hooks/useMetadataTiers';
import { useBilling } from '../../contexts/BillingContext';

/**
 * Tier Trigger Controls Component
 * Provides manual trigger buttons for tier collections
 */
const TierTriggerControls = ({ connectionId, compact = false, onTriggerSuccess }) => {
  const [loadingTier, setLoadingTier] = useState(null);
  const triggerTier = useTriggerTier(connectionId);
  const triggerAllTiers = useTriggerAllTiers(connectionId);
  const { isFeatureEnabled, getUpgradeUrl, getLimitStatus, plan } = useBilling();

  // Check which tiers are enabled for this plan.
  //
  // With BILLING_GATING_ACTIVE=false (single-price era, May 2026+),
  // isFeatureEnabled returns true unconditionally, so all of the
  // tierN-enabled booleans below collapse to true. The `requiredPlan`
  // fields and the LOCKED-state JSX (rendered conditionally on
  // !tier.enabled) become unreachable but stay in place as gating
  // bones for the day tier-based pricing returns.
  //
  // Reference (for when gating is re-enabled): enabled_tiers array
  // from backend.plans — Free=[1,2], Starter=[1,2,3], Growth=[1,2,3,4,5],
  // Scale=[1,2,3,4,5]. Tier 5 (Lineage) is also gated by the separate
  // lineage_enabled feature flag.
  const enabledTiers = plan?.features?.enabled_tiers || [1, 2];
  const tier3Enabled = enabledTiers.includes(3) || isFeatureEnabled('tier3_enabled');
  const tier4Enabled = enabledTiers.includes(4) || isFeatureEnabled('tier4_enabled');
  const tier5LineageEnabled = isFeatureEnabled('lineage_enabled');
  const profiledTableStatus = getLimitStatus('profiled_tables');

  // Tier 6 (usage analysis) is intentionally absent from this list:
  // the collector is not implemented (workers/unified_worker.py:493
  // returns False for tier6_usage tasks) — leaving it visible would
  // let users queue jobs that fail all 3 retries, burning Supabase +
  // BigQuery cycles. Re-add when the collector ships. See
  // backend/core/billing/plans.py:184 for the matching backend note.
  const tiers = [
    { key: 'tier1_discovery', name: 'Tier 1', label: 'Discovery', description: 'Discover tables and views', enabled: true },
    { key: 'tier2_schema', name: 'Tier 2', label: 'Schema', description: 'Collect column metadata', enabled: true },
    { key: 'tier3_changes', name: 'Tier 3', label: 'Changes', description: 'Detect changed tables', enabled: tier3Enabled, requiredPlan: 'Starter' },
    { key: 'tier4_profiling', name: 'Tier 4', label: 'Profiling', description: 'Profile column statistics', enabled: tier4Enabled, requiredPlan: 'Growth' },
    { key: 'tier5_lineage', name: 'Tier 5', label: 'Lineage', description: 'Discover data lineage', enabled: tier5LineageEnabled, requiredPlan: 'Starter' },
  ];

  const handleTriggerTier = async (tierType) => {
    setLoadingTier(tierType);

    try {
      await triggerTier.mutateAsync({ tierType });

      if (onTriggerSuccess) {
        onTriggerSuccess(tierType);
      }
    } catch (error) {
      console.error('Failed to trigger tier:', error);
    } finally {
      setLoadingTier(null);
    }
  };

  const handleTriggerAll = async () => {
    setLoadingTier('all');

    try {
      await triggerAllTiers.mutateAsync();

      if (onTriggerSuccess) {
        onTriggerSuccess('all');
      }
    } catch (error) {
      console.error('Failed to trigger all tiers:', error);
    } finally {
      setLoadingTier(null);
    }
  };

  if (compact) {
    // Compact view - just a refresh all button
    return (
      <button
        onClick={handleTriggerAll}
        disabled={loadingTier === 'all'}
        className="btn btn-sm btn-outline gap-2"
      >
        <ArrowPathIcon className={`w-4 h-4 ${loadingTier === 'all' ? 'animate-spin' : ''}`} />
        {loadingTier === 'all' ? 'Refreshing...' : 'Refresh All'}
      </button>
    );
  }

  // Full view with individual tier controls
  return (
    <div className="bg-white rounded-lg border border-gray-200 p-6">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h3 className="text-lg font-semibold text-gray-900">Manual Triggers</h3>
          <p className="text-sm text-gray-500 mt-1">
            Manually trigger tier collections or refresh all metadata
          </p>
        </div>
      </div>

      {/* Individual Tier Triggers */}
      <div className="space-y-3 mb-4">
        {tiers.map((tier) => (
          <div
            key={tier.key}
            className={`flex items-center justify-between p-4 rounded-lg border transition-colors ${
              tier.enabled
                ? 'border-gray-200 hover:border-gray-300'
                : 'border-gray-100 bg-gray-50'
            }`}
          >
            <div className="flex-1">
              <div className="flex items-center gap-2 mb-1">
                <span className={`font-semibold ${tier.enabled ? 'text-gray-900' : 'text-gray-400'}`}>
                  {tier.name}
                </span>
                <span className={`text-sm ${tier.enabled ? 'text-gray-600' : 'text-gray-400'}`}>
                  - {tier.label}
                </span>
                {!tier.enabled && (
                  <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-amber-100 text-amber-800">
                    <LockClosedIcon className="h-3 w-3 mr-1" />
                    {tier.requiredPlan}+
                  </span>
                )}
              </div>
              <div className={`text-sm ${tier.enabled ? 'text-gray-500' : 'text-gray-400'}`}>
                {tier.description}
              </div>
            </div>

            {tier.enabled ? (
              <button
                onClick={() => handleTriggerTier(tier.key)}
                disabled={loadingTier === tier.key || loadingTier === 'all'}
                className="btn btn-sm btn-outline gap-2"
              >
                {loadingTier === tier.key ? (
                  <>
                    <ArrowPathIcon className="w-4 h-4 animate-spin" />
                    Running...
                  </>
                ) : (
                  <>
                    <PlayIcon className="w-4 h-4" />
                    Trigger
                  </>
                )}
              </button>
            ) : (
              <Link
                to={getUpgradeUrl()}
                className="btn btn-sm gap-2 bg-primary-50 text-primary-700 hover:bg-primary-100 border-primary-200"
              >
                <SparklesIcon className="w-4 h-4" />
                Upgrade
              </Link>
            )}
          </div>
        ))}
      </div>

      {/* Profiled tables limit warning */}
      {tier4Enabled && !profiledTableStatus.isUnlimited && profiledTableStatus.is_near_limit && (
        <div className="mb-4 p-3 rounded-lg bg-amber-50 border border-amber-200">
          <div className="flex items-center gap-2 text-sm text-amber-800">
            <ExclamationCircleIcon className="w-5 h-5 text-amber-500" />
            <span>
              Profiled tables: {profiledTableStatus.current}/{profiledTableStatus.max} ({Math.round(profiledTableStatus.percentage)}% used)
            </span>
            <Link to={getUpgradeUrl()} className="ml-auto font-medium text-amber-700 hover:text-amber-900">
              Upgrade for more →
            </Link>
          </div>
        </div>
      )}

      {/* Trigger All Button */}
      <div className="pt-4 border-t border-gray-200">
        <button
          onClick={handleTriggerAll}
          disabled={loadingTier !== null}
          className="btn btn-primary w-full gap-2"
        >
          {loadingTier === 'all' ? (
            <>
              <ArrowPathIcon className="w-5 h-5 animate-spin" />
              Refreshing All Tiers...
            </>
          ) : (
            <>
              <ArrowPathIcon className="w-5 h-5" />
              Refresh All Tiers
            </>
          )}
        </button>

        <p className="text-xs text-gray-500 mt-2 text-center">
          This will trigger all 5 tiers sequentially for a complete refresh
        </p>
      </div>

      {/* Success/Error Messages */}
      {triggerTier.isSuccess && !loadingTier && (
        <div className="mt-4 p-3 rounded-lg bg-green-50 border border-green-200 flex items-center gap-2">
          <CheckCircleIcon className="w-5 h-5 text-green-600" />
          <span className="text-sm text-gray-900 font-medium">Tier triggered successfully</span>
        </div>
      )}

      {triggerTier.isError && !loadingTier && (
        <div className="mt-4 p-3 rounded-lg bg-error bg-opacity-10 border border-error flex items-center gap-2">
          <ExclamationCircleIcon className="w-5 h-5 text-white" />
          <span className="text-sm text-white font-medium">
            Failed to trigger tier: {
              typeof triggerTier.error?.message === 'string'
                ? triggerTier.error.message
                : typeof triggerTier.error?.response?.data?.error === 'object'
                ? triggerTier.error.response.data.error.message || JSON.stringify(triggerTier.error.response.data.error)
                : triggerTier.error?.response?.data?.error
                || JSON.stringify(triggerTier.error?.message)
                || 'Unknown error'
            }
          </span>
        </div>
      )}

      {triggerAllTiers.isSuccess && !loadingTier && (
        <div className="mt-4 p-3 rounded-lg bg-green-50 border border-green-200 flex items-center gap-2">
          <CheckCircleIcon className="w-5 h-5 text-green-600" />
          <span className="text-sm text-gray-900 font-medium">All tiers triggered successfully</span>
        </div>
      )}

      {triggerAllTiers.isError && !loadingTier && (
        <div className="mt-4 p-3 rounded-lg bg-error bg-opacity-10 border border-error flex items-center gap-2">
          <ExclamationCircleIcon className="w-5 h-5 text-white" />
          <span className="text-sm text-white font-medium">
            Failed to trigger all tiers: {
              typeof triggerAllTiers.error?.message === 'string'
                ? triggerAllTiers.error.message
                : typeof triggerAllTiers.error?.response?.data?.error === 'object'
                ? triggerAllTiers.error.response.data.error.message || JSON.stringify(triggerAllTiers.error.response.data.error)
                : triggerAllTiers.error?.response?.data?.error
                || JSON.stringify(triggerAllTiers.error?.message)
                || 'Unknown error'
            }
          </span>
        </div>
      )}
    </div>
  );
};

export default TierTriggerControls;
